import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { hasAnyRole } from '../../utils/roles';
import {
  PlannerCard,
  PlannerError,
  PlannerHeading,
  PlannerInput,
  PlannerKicker,
  PlannerPage,
  PlannerSubtle,
  plannerColors,
} from './plannerTheme';
import {
  getSongTemplateDurationInputValue,
  parseSongTemplateDurationInput,
} from './songDuration';

interface SongTemplate {
  id: string;
  title: string;
  artist?: string | null;
  key_signature?: string | null;
  length_minutes: number;
  length_seconds?: number | null;
  bpm?: number | null;
  sequence: number;
  song_sequence?: string | null;
  notes?: string | null;
  lyrics?: string | null;
  lyrics_url?: string | null;
  chord_chart_url?: string | null;
  video_url?: string | null;
  themes?: string[] | null;
  last_scheduled_date?: string | null;
  is_active: boolean;
}

interface SongTemplateMediaItem {
  id: string;
  song_template_id: string;
  title: string;
  public_url: string;
  mime_type: string;
  file_size?: number | null;
  created_at?: string;
}

const MAX_AUDIO_BYTES = 25 * 1024 * 1024;

const FormCard = styled(PlannerCard)`
  display: grid;
  gap: 0.85rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const BreadcrumbLink = styled(Link)`
  color: ${plannerColors.accent};
  font-size: 0.8rem;
  text-decoration: none;
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: ${plannerColors.text};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 700;
`;

const TextArea = styled.textarea`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 10px;
  padding: 0.62rem 0.75rem;
  min-height: 92px;
  resize: vertical;
`;

const SequenceTokenRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const SequenceTokenButton = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 999px;
  padding: 0.3rem 0.56rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  border: 1px solid rgba(241, 226, 198, 0.5);
  background: linear-gradient(140deg, #f1e2c6 0%, #dcc19a 100%);
  color: #171717;
  border-radius: 10px;
  padding: 0.6rem 0.95rem;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 10px;
  padding: 0.56rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
`;

const DangerButton = styled.button`
  border: 1px solid rgba(255, 69, 58, 0.35);
  background: rgba(255, 69, 58, 0.13);
  color: #ffd6d3;
  border-radius: 10px;
  padding: 0.56rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
`;

const EmptyState = styled(PlannerCard)`
  text-align: center;
`;

const DiscographyHero = styled(PlannerCard)`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 1rem;
  align-items: center;
  background:
    radial-gradient(circle at 18% 20%, rgba(229, 211, 179, 0.24), transparent 40%),
    linear-gradient(135deg, rgba(37, 37, 40, 0.95), rgba(25, 25, 28, 0.98));

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const CoverArt = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 18px;
  border: 1px solid rgba(229, 211, 179, 0.28);
  background:
    radial-gradient(circle at 25% 20%, rgba(229, 211, 179, 0.35), transparent 46%),
    linear-gradient(140deg, #303035 0%, #1f1f22 52%, #17171a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${plannerColors.accent};
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: 0.06em;

  @media (max-width: 760px) {
    width: 100%;
    max-width: 240px;
    height: 240px;
    justify-self: center;
  }
`;

const DiscographyMeta = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const SongTitle = styled.h2`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 1.8rem;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const ArtistName = styled.p`
  margin: 0;
  color: ${plannerColors.accent};
  font-size: 1.02rem;
  font-weight: 600;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const Chip = styled.span`
  border: 1px solid rgba(229, 211, 179, 0.25);
  background: rgba(255, 255, 255, 0.04);
  color: ${plannerColors.text};
  border-radius: 999px;
  padding: 0.3rem 0.62rem;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ViewActions = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const SectionGrid = styled.div`
  display: grid;
  gap: 0.9rem;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled(PlannerCard)`
  display: grid;
  gap: 0.6rem;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.92rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const SequenceItemList = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const SequenceItem = styled.span`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  border-radius: 8px;
  color: ${plannerColors.text};
  font-size: 0.78rem;
  padding: 0.28rem 0.48rem;
`;

const LongText = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${plannerColors.text};
  font-size: 0.86rem;
  line-height: 1.5;
  font-family: inherit;
`;

const ResourceLink = styled.a`
  color: ${plannerColors.accent};
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const AudioUploadInput = styled.input`
  display: none;
`;

const AudioList = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const AudioRow = styled.div`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  border-radius: 10px;
  padding: 0.58rem 0.62rem;
  display: grid;
  gap: 0.5rem;
`;

const AudioTitle = styled.p`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 0.86rem;
  font-weight: 700;
`;

const AudioMeta = styled.p`
  margin: 0;
  color: ${plannerColors.muted};
  font-size: 0.75rem;
`;

const AudioActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const AudioPlayer = styled.audio`
  width: 100%;
  max-width: 420px;
`;

const initialForm = {
  title: '',
  artist: '',
  keySignature: '',
  lengthMinutes: '0',
  bpm: '',
  sortOrder: '0',
  songSequence: '',
  notes: '',
  lyrics: '',
  lyricsUrl: '',
  chordChartUrl: '',
  videoUrl: '',
  themes: '',
  lastScheduledDate: '',
};

const sequenceTokens = [
  'Intro',
  'Verse 1',
  'Verse 2',
  'Pre-Chorus',
  'Chorus',
  'Bridge',
  'Vamp',
  'Inst',
  'Tag',
  'Outro',
];

function renderValue(value: string): string {
  const trimmed = value.trim();
  return trimmed || '-';
}

function splitSequence(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function getCoverInitials(title: string, artist: string): string {
  const source = (title || artist).trim();
  if (!source) return 'SP';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function parseThemes(value: string): string[] | null {
  const normalized = value.trim();
  if (!normalized) return null;
  const parts = normalized
    .split(',')
    .map((theme) => theme.trim())
    .filter((theme) => theme.length > 0);
  return parts.length ? parts : null;
}

export default function ChurchPlannerSongDetailsPage() {
  const { songId } = useParams<{ songId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [audioItems, setAudioItems] = useState<SongTemplateMediaItem[]>([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [deletingAudioId, setDeletingAudioId] = useState<string | null>(null);
  const audioInputRef = useRef<HTMLInputElement | null>(null);
  const sequenceItems = splitSequence(form.songSequence);
  const canManageSongAudio = hasAnyRole(
    profile?.roles ?? user?.app_metadata?.roles ?? user?.user_metadata?.roles,
    ['service_planner_admin', 'admin', 'super_admin'],
  );

  const formatFileSize = (size?: number | null) => {
    if (!size || size <= 0) return 'Unknown size';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadSongAudio = async (targetSongId: string) => {
    setAudioLoading(true);
    const { data, error } = await supabase
      .from('service_song_template_media_items')
      .select('*')
      .eq('song_template_id', targetSongId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    setAudioLoading(false);
    if (error) {
      setErrorText(error.message || 'Unable to load song audio files.');
      return;
    }

    setAudioItems((data as SongTemplateMediaItem[]) ?? []);
  };

  useEffect(() => {
    const loadSong = async () => {
      if (!songId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorText('');

      const { data, error } = await supabase
        .from('service_song_templates')
        .select('*')
        .eq('id', songId)
        .maybeSingle();

      setLoading(false);

      if (error) {
        setErrorText(error.message || 'Unable to load song template.');
        return;
      }

      if (!data) {
        setNotFound(true);
        return;
      }

      const song = data as SongTemplate;
      setForm({
        title: song.title || '',
        artist: song.artist || '',
        keySignature: song.key_signature || '',
        lengthMinutes: getSongTemplateDurationInputValue(song),
        bpm: song.bpm == null ? '' : String(song.bpm),
        sortOrder: String(song.sequence ?? 0),
        songSequence: song.song_sequence || '',
        notes: song.notes || '',
        lyrics: song.lyrics || '',
        lyricsUrl: song.lyrics_url || '',
        chordChartUrl: song.chord_chart_url || '',
        videoUrl: song.video_url || '',
        themes: (song.themes || []).join(', '),
        lastScheduledDate: song.last_scheduled_date || '',
      });

      await loadSongAudio(song.id);
    };

    void loadSong();
  }, [songId]);

  const saveSong = async () => {
    if (!songId) return;

    const title = form.title.trim();
    const sequence = Number.parseInt(form.sortOrder.trim(), 10);
    const bpm = form.bpm.trim() ? Number.parseInt(form.bpm.trim(), 10) : null;
    const parsedDuration = parseSongTemplateDurationInput(form.lengthMinutes);

    if (!title) {
      setErrorText('Song title is required.');
      return;
    }

    if (!parsedDuration.value) {
      setErrorText(parsedDuration.error || 'Duration must be minutes (5) or m:ss (4:53).');
      return;
    }

    if (!Number.isFinite(sequence) || sequence < 0) {
      setErrorText('Sequence must be 0 or greater.');
      return;
    }

    if (bpm != null && (!Number.isFinite(bpm) || bpm <= 0)) {
      setErrorText('BPM must be greater than 0.');
      return;
    }

    setSaving(true);
    setErrorText('');

    const { error } = await supabase
      .from('service_song_templates')
      .update({
        title,
        artist: form.artist.trim() || null,
        key_signature: form.keySignature.trim() || null,
        length_minutes: parsedDuration.value.lengthMinutes,
        length_seconds: parsedDuration.value.lengthSeconds,
        bpm,
        sequence,
        song_sequence: form.songSequence.trim() || null,
        notes: form.notes.trim() || null,
        lyrics: form.lyrics.trim() || null,
        lyrics_url: form.lyricsUrl.trim() || null,
        chord_chart_url: form.chordChartUrl.trim() || null,
        video_url: form.videoUrl.trim() || null,
        themes: parseThemes(form.themes),
        last_scheduled_date: form.lastScheduledDate.trim() || null,
      })
      .eq('id', songId);

    setSaving(false);

    if (error) {
      setErrorText(error.message || 'Unable to save song template.');
      return;
    }

    setIsEditing(false);
  };

  const deleteSong = async () => {
    if (!songId) return;

    setDeleting(true);
    setErrorText('');

    const { error } = await supabase.from('service_song_templates').delete().eq('id', songId);

    setDeleting(false);

    if (error) {
      setErrorText(error.message || 'Unable to delete song template.');
      return;
    }

    navigate('/church-planner/songs');
  };

  const handleAudioUpload = async (file: File | null | undefined) => {
    if (!file || !songId) return;

    const lowerName = file.name.toLowerCase();
    const mimeType = (file.type || '').toLowerCase();
    if (!lowerName.endsWith('.mp3') || (mimeType && mimeType !== 'audio/mpeg' && mimeType !== 'audio/mp3')) {
      setErrorText('Only MP3 files are allowed.');
      return;
    }

    if (file.size > MAX_AUDIO_BYTES) {
      setErrorText('MP3 files must be 25 MB or smaller.');
      return;
    }

    setAudioUploading(true);
    setErrorText('');

    const formData = new FormData();
    formData.append('action', 'upload');
    formData.append('songId', songId);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('file', file);

    const { data, error } = await supabase.functions.invoke('song-audio-upload', {
      body: formData,
    });

    setAudioUploading(false);

    if (error || !data?.success) {
      setErrorText(error?.message || data?.error || 'Unable to upload MP3 right now.');
      return;
    }

    if (audioInputRef.current) {
      audioInputRef.current.value = '';
    }

    await loadSongAudio(songId);
  };

  const handleAudioDelete = async (item: SongTemplateMediaItem) => {
    if (!window.confirm(`Delete ${item.title}?`)) return;

    setDeletingAudioId(item.id);
    const { data, error } = await supabase.functions.invoke('song-audio-upload', {
      body: {
        action: 'delete',
        mediaId: item.id,
      },
    });
    setDeletingAudioId(null);

    if (error || !data?.success) {
      setErrorText(error?.message || data?.error || 'Unable to delete MP3 right now.');
      return;
    }

    if (songId) {
      await loadSongAudio(songId);
    }
  };

  if (loading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerSubtle>Loading song template...</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (notFound) {
    return (
      <PlannerPage>
        <EmptyState>
          <PlannerKicker>Planner Music</PlannerKicker>
          <PlannerHeading>Song Not Found</PlannerHeading>
          <PlannerSubtle>This song may have been deleted or the link is invalid.</PlannerSubtle>
          <ButtonRow style={{ justifyContent: 'center', marginTop: '0.9rem' }}>
            <GhostButton type="button" onClick={() => navigate('/church-planner/songs')}>
              Back to Songs
            </GhostButton>
          </ButtonRow>
        </EmptyState>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      <PlannerCard>
        <HeaderRow>
          <PlannerKicker style={{ margin: 0 }}>Planner Music</PlannerKicker>
          <BreadcrumbLink to="/church-planner/songs">Back to Songs</BreadcrumbLink>
        </HeaderRow>
        <PlannerHeading style={{ marginTop: '0.35rem' }}>{isEditing ? 'Edit Song Template' : 'View Song'}</PlannerHeading>
        <PlannerSubtle>Song ID: {songId}</PlannerSubtle>
      </PlannerCard>

      {!isEditing ? (
        <>
          <DiscographyHero>
            <CoverArt>{getCoverInitials(form.title, form.artist)}</CoverArt>
            <DiscographyMeta>
              <div>
                <SongTitle>{renderValue(form.title)}</SongTitle>
                <ArtistName>{renderValue(form.artist)}</ArtistName>
              </div>
              <ChipRow>
                <Chip>{renderValue(form.lengthMinutes)}</Chip>
                <Chip>{renderValue(form.bpm)} BPM</Chip>
                <Chip>Key {renderValue(form.keySignature)}</Chip>
                <Chip>Sort {renderValue(form.sortOrder)}</Chip>
                <Chip>{form.lastScheduledDate.trim() ? `Last scheduled ${form.lastScheduledDate}` : 'No schedule yet'}</Chip>
              </ChipRow>
              {form.themes.trim() ? (
                <ChipRow>
                  {form.themes.split(',').map((theme) => (
                    <Chip key={theme.trim()}>{theme.trim()}</Chip>
                  ))}
                </ChipRow>
              ) : null}
              <ViewActions>
                <PrimaryButton type="button" onClick={() => setIsEditing(true)}>
                  Edit Song
                </PrimaryButton>
                <GhostButton type="button" onClick={() => navigate('/church-planner/songs')}>
                  Back to Songs
                </GhostButton>
              </ViewActions>
            </DiscographyMeta>
          </DiscographyHero>

          <SectionGrid>
            <SectionCard>
              <SectionTitle>Song Sequence</SectionTitle>
              {sequenceItems.length ? (
                <SequenceItemList>
                  {sequenceItems.map((item, index) => (
                    <SequenceItem key={`${item}-${index}`}>{item}</SequenceItem>
                  ))}
                </SequenceItemList>
              ) : (
                <PlannerSubtle>No sequence added yet.</PlannerSubtle>
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle>Resources</SectionTitle>
              {form.videoUrl.trim() ? (
                <ResourceLink href={form.videoUrl.trim()} target="_blank" rel="noreferrer">
                  Open Video URL
                </ResourceLink>
              ) : (
                <PlannerSubtle>No video URL</PlannerSubtle>
              )}
              {form.lyricsUrl.trim() ? (
                <ResourceLink href={form.lyricsUrl.trim()} target="_blank" rel="noreferrer">
                  Open Lyrics URL
                </ResourceLink>
              ) : (
                <PlannerSubtle>No lyrics URL</PlannerSubtle>
              )}
              {form.chordChartUrl.trim() ? (
                <ResourceLink href={form.chordChartUrl.trim()} target="_blank" rel="noreferrer">
                  Open Chord Chart URL
                </ResourceLink>
              ) : (
                <PlannerSubtle>No chord chart URL</PlannerSubtle>
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle>Song Audio</SectionTitle>
              <AudioUploadInput
                ref={audioInputRef}
                type="file"
                accept="audio/mpeg,.mp3"
                onChange={(event) => void handleAudioUpload(event.target.files?.[0])}
              />
              {canManageSongAudio ? (
                <ButtonRow>
                  <PrimaryButton
                    type="button"
                    onClick={() => audioInputRef.current?.click()}
                    disabled={audioUploading}
                  >
                    {audioUploading ? 'Uploading...' : 'Upload MP3'}
                  </PrimaryButton>
                </ButtonRow>
              ) : null}

              {audioLoading ? <PlannerSubtle>Loading audio files...</PlannerSubtle> : null}
              {!audioLoading && audioItems.length === 0 ? <PlannerSubtle>No MP3 files uploaded yet.</PlannerSubtle> : null}

              {audioItems.length ? (
                <AudioList>
                  {audioItems.map((item) => (
                    <AudioRow key={item.id}>
                      <div>
                        <AudioTitle>{item.title}</AudioTitle>
                        <AudioMeta>{formatFileSize(item.file_size)}</AudioMeta>
                      </div>
                      <AudioPlayer controls preload="none" src={item.public_url} />
                      <AudioActions>
                        {canManageSongAudio ? (
                          <DangerButton
                            type="button"
                            onClick={() => void handleAudioDelete(item)}
                            disabled={deletingAudioId === item.id}
                          >
                            {deletingAudioId === item.id ? 'Deleting...' : 'Delete'}
                          </DangerButton>
                        ) : null}
                      </AudioActions>
                    </AudioRow>
                  ))}
                </AudioList>
              ) : null}
            </SectionCard>

            <SectionCard>
              <SectionTitle>Notes</SectionTitle>
              {form.notes.trim() ? <LongText>{form.notes}</LongText> : <PlannerSubtle>No notes added yet.</PlannerSubtle>}
            </SectionCard>

            <SectionCard>
              <SectionTitle>Lyrics</SectionTitle>
              {form.lyrics.trim() ? <LongText>{form.lyrics}</LongText> : <PlannerSubtle>No lyrics stored yet.</PlannerSubtle>}
            </SectionCard>
          </SectionGrid>
        </>
      ) : (
        <FormCard>
        <FormRow>
          <Label>
            Title
            <PlannerInput value={form.title} onChange={(event) => setForm((curr) => ({ ...curr, title: event.target.value }))} />
          </Label>
          <Label>
            Artist
            <PlannerInput value={form.artist} onChange={(event) => setForm((curr) => ({ ...curr, artist: event.target.value }))} />
          </Label>
        </FormRow>

        <FormRow>
          <Label>
            Key
            <PlannerInput value={form.keySignature} onChange={(event) => setForm((curr) => ({ ...curr, keySignature: event.target.value }))} />
          </Label>
          <Label>
            Duration (m:ss or minutes)
            <PlannerInput value={form.lengthMinutes} onChange={(event) => setForm((curr) => ({ ...curr, lengthMinutes: event.target.value }))} placeholder="4:53 or 5" />
          </Label>
        </FormRow>

        <FormRow>
          <Label>
            BPM
            <PlannerInput value={form.bpm} onChange={(event) => setForm((curr) => ({ ...curr, bpm: event.target.value }))} />
          </Label>
          <Label>
            Sort Order
            <PlannerInput value={form.sortOrder} onChange={(event) => setForm((curr) => ({ ...curr, sortOrder: event.target.value }))} />
          </Label>
        </FormRow>

        <Label>
          Song Sequence
          <TextArea
            value={form.songSequence}
            onChange={(event) => setForm((curr) => ({ ...curr, songSequence: event.target.value }))}
            placeholder="Intro, Verse 1 2x, Pre-Chorus 1x, Chorus 1x, Verse 2, Vamp, Inst"
          />
        </Label>

        <SequenceTokenRow>
          {sequenceTokens.map((token) => (
            <SequenceTokenButton
              key={token}
              type="button"
              onClick={() => {
                setForm((curr) => {
                  const base = curr.songSequence.trim();
                  return {
                    ...curr,
                    songSequence: base ? `${base}, ${token}` : token,
                  };
                });
              }}
            >
              {token}
            </SequenceTokenButton>
          ))}
        </SequenceTokenRow>

        <FormRow>
          <Label>
            Themes (comma separated)
            <PlannerInput value={form.themes} onChange={(event) => setForm((curr) => ({ ...curr, themes: event.target.value }))} />
          </Label>
          <Label>
            Last Scheduled Date
            <PlannerInput value={form.lastScheduledDate} onChange={(event) => setForm((curr) => ({ ...curr, lastScheduledDate: event.target.value }))} placeholder="YYYY-MM-DD" />
          </Label>
        </FormRow>

        <FormRow>
          <Label>
            Lyrics URL
            <PlannerInput value={form.lyricsUrl} onChange={(event) => setForm((curr) => ({ ...curr, lyricsUrl: event.target.value }))} />
          </Label>
          <Label>
            Chord Chart URL
            <PlannerInput value={form.chordChartUrl} onChange={(event) => setForm((curr) => ({ ...curr, chordChartUrl: event.target.value }))} />
          </Label>
        </FormRow>

        <Label>
          Video URL
          <PlannerInput value={form.videoUrl} onChange={(event) => setForm((curr) => ({ ...curr, videoUrl: event.target.value }))} />
        </Label>

        <Label>
          Notes
          <TextArea value={form.notes} onChange={(event) => setForm((curr) => ({ ...curr, notes: event.target.value }))} />
        </Label>

        <Label>
          Lyrics
          <TextArea value={form.lyrics} onChange={(event) => setForm((curr) => ({ ...curr, lyrics: event.target.value }))} />
        </Label>

        <ButtonRow>
          <PrimaryButton type="button" onClick={() => void saveSong()} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </PrimaryButton>
          <GhostButton type="button" onClick={() => setIsEditing(false)}>
            Cancel Edit
          </GhostButton>
          <DangerButton type="button" onClick={() => void deleteSong()} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete Song'}
          </DangerButton>
        </ButtonRow>
      </FormCard>
      )}

      {errorText ? <PlannerError>{errorText}</PlannerError> : null}
    </PlannerPage>
  );
}
